import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Dialog, IconButton } from '@ctct/ui-react';

import './FeatureUpgradeDialog.scss';
import useInitalApiCalls from './hooks/useInitalApiCalls';
import useInitPlanBlocks from './hooks/useInitPlanBlocks';
import useRedirectTo from './hooks/useRedirectTo';
import useStripeScheduledDowngrade from './hooks/useStripeScheduledDowngrade';
import useTrackActions from './hooks/useTrackActions';
import { useUpgradeContext } from './UpgradeContext';
import UpgradeStepStrategy from './UpgradeStepStrategy';
import { stepsEnum } from './utils';
import { isUnknown } from './utils/isUnknown';
import { distUiGoTo, getIsSingleBilled } from '../../api/DistUI';

const FeatureUpgradeDialog = ({
  isOpen,
  setIsOpen,
  setCloseDialog,
  stepOneInformation,
  feature,
  activity,
  usage,
  featureRequirements,
  launchPoint,
  paymentSuccessCallback,
  unsavedChangesInCallingPage,
  wrapClassName,
  returnURL,
  returnLabel,
  isFullScreen,
  calledFromDevPage,
  promoCode,
}) => {
  const { t } = useTranslation();
  const [
    {
      upgradeStep,
      isTwoStepDialog,
      confirmProceedCallback,
      upgradeIsTrialer,
      upgradeIsSuspended,
      upgradeReturnURL,
      upgradeReturnLabel,
      recommendedUpgradePlanPromotion,
      comparePlanBlocks,
      isSingleBill,
      isStripeAccount,
    },
    dispatchUpgrade,
  ] = useUpgradeContext();

  const setUpgradeStep = (step) => {
    dispatchUpgrade({ type: 'setUpgradeStep', value: step });
  };

  const redirectTo = useRedirectTo({ upgradeReturnURL, disableRedirect: calledFromDevPage });

  const {
    hasScheduledDowngrade,
    isLoading: isScheduledDowngradeLoading,
    cancelScheduledDowngrade,
  } = useStripeScheduledDowngrade({
    isOpen,
    isStripeAccount,
  });

  const { trackActionOnOpen, trackActionOnClose, trackLeaveActionOnProceed, trackLeaveActionOnStay } = useTrackActions({
    feature,
    featureRequirements,
    activity,
    isTwoStepDialog,
    launchPoint,
  });

  const closeDialog = () => {
    trackActionOnClose();
    setIsOpen(false);
    setCloseDialog(upgradeStep === stepsEnum.SUCCESS);
    setUpgradeStep(stepsEnum.INTRO);

    if (isUnknown(feature)) {
      redirectTo('dashboard');
    }
  };

  useEffect(() => {
    // initialize
    if (isOpen) {
      getIsSingleBilled().then((singleBilled) => dispatchUpgrade({ type: 'setIsSingleBill', value: singleBilled }));
      setUpgradeStep(stepOneInformation ? stepsEnum.INTRO : stepsEnum.UPGRADE);
      dispatchUpgrade({ type: 'setstepOneInformation', value: stepOneInformation });
      dispatchUpgrade({ type: 'setWrapClassName', value: wrapClassName });
      dispatchUpgrade({ type: 'setPaymentSuccessCallback', value: paymentSuccessCallback });
      dispatchUpgrade({ type: 'setUpgradeReturnURL', value: returnURL || '' });
      dispatchUpgrade({ type: 'setUpgradeReturnLabel', value: returnLabel || '' });
      dispatchUpgrade({ type: 'setIsTwoStepDialog', value: !!stepOneInformation });
    }
  }, [isOpen]);

  useEffect(() => {
    // initialize
    dispatchUpgrade({ type: 'setUnsavedChangesInCallingPage', value: unsavedChangesInCallingPage });
  }, [unsavedChangesInCallingPage]);

  const redirectToBillingHome = useCallback(() => {
    redirectTo('billingHome');
  }, [redirectTo]);

  const { isLoaded: isInitialDataLoaded } = useInitalApiCalls({
    usage,
    featureRequirements,
    feature,
    activity,
    redirectToBillingHome,
    promoCode,
  });

  useInitPlanBlocks();

  useEffect(() => {
    if (
      isOpen &&
      isInitialDataLoaded &&
      (upgradeIsTrialer || upgradeIsSuspended) &&
      upgradeReturnURL != null &&
      (isTwoStepDialog === false || (isTwoStepDialog === true && upgradeStep === stepsEnum.UPGRADE))
    ) {
      const payForServices = () => {
        distUiGoTo(isStripeAccount ? 'plansAndPricing' : 'payForServices', {
          returnURL: upgradeReturnURL,
          returnLabel: upgradeReturnLabel,
        });
      };

      if (unsavedChangesInCallingPage) {
        dispatchUpgrade({ type: 'setConfirmProceedCallback', value: payForServices });
      } else payForServices();
    }
  }, [
    isOpen,
    isInitialDataLoaded,
    isStripeAccount,
    upgradeIsTrialer,
    upgradeIsSuspended,
    isTwoStepDialog,
    upgradeStep,
  ]);

  useEffect(() => {
    if (isOpen && isTwoStepDialog != null) {
      trackActionOnOpen();
    }
  }, [isOpen, isTwoStepDialog]);

  const isLegacyMigrationDialogOneStep =
    upgradeStep === stepsEnum.UPGRADE &&
    !isTwoStepDialog &&
    !upgradeIsTrialer &&
    !upgradeIsSuspended &&
    Boolean(recommendedUpgradePlanPromotion);

  const upgradeStepStrategyProps = {
    upgradeStep,
    isTwoStepDialog,
    upgradeIsTrialer,
    upgradeIsSuspended,
    closeDialog,
    hasScheduledDowngrade,
  };

  return (
    <>
      {confirmProceedCallback && (
        <Dialog
          isOpen={confirmProceedCallback}
          data-qe-id="confirm-leaving-feature-upgrade-dialog"
          heading={t('upgrade-popup.confirm')}
          wrapClassName={wrapClassName}
          footerActions={
            <>
              <Button
                data-qe-id="confirm-leaving-feature-upgrade-dialog-button-stay"
                shape="pill"
                text={t('upgrade-popup.button-text.stay')}
                kind="primary"
                onClick={() => {
                  trackLeaveActionOnStay();
                  dispatchUpgrade({ type: 'setConfirmProceedCallback', value: null });

                  if (upgradeIsTrialer || upgradeIsSuspended) {
                    if (isTwoStepDialog) {
                      // stay on intro step
                      dispatchUpgrade({ type: 'setUpgradeStep', value: stepsEnum.INTRO });
                    } else {
                      // close
                      closeDialog();
                    }
                  }
                }}
              />
              <Button
                data-qe-id="confirm-leaving-feature-upgrade-dialog-button-leave"
                shape="pill"
                text={t('upgrade-popup.button-text.continue')}
                onClick={() => {
                  trackLeaveActionOnProceed();
                  confirmProceedCallback();
                }}
              />
            </>
          }
        >
          {t('upgrade-popup.sure-you-want-to-leave')}
        </Dialog>
      )}
      {((upgradeIsTrialer === false && upgradeIsSuspended === false) || isTwoStepDialog) && (
        <Dialog
          className={`feature-upgrade-popup ${isTwoStepDialog ? 'two-step' : 'one-step'} ${
            isFullScreen ? 'full-screen' : ''
          } ${
            upgradeStep === stepsEnum.INTRO || ((upgradeIsTrialer || upgradeIsSuspended) && isTwoStepDialog)
              ? 'step-one'
              : 'step-two'
          }`}
          data-qe-id="feature-upgrade-dialog"
          wrapClassName={classNames(wrapClassName, 'ctct-theme')}
          kind={isFullScreen ? 'takeover' : 'jumbo'}
          heading={
            isLegacyMigrationDialogOneStep
              ? t('upgrade-popup.upgrade-to', { planName: comparePlanBlocks?.[1]?.planBlockInfo.planName ?? '' })
              : t('upgrade-popup.upgrade-your-plan')
          }
          headerActions={
            isTwoStepDialog &&
            upgradeStep === stepsEnum.UPGRADE && (
              <IconButton
                data-qe-id="cancel-dialog-header-back-button"
                glyph="arrowWest"
                title={t('upgrade-popup.back')}
                aria-label="Back"
                onClick={() => {
                  setUpgradeStep(stepsEnum.INTRO);
                }}
              />
            )
          }
          isOpen={isOpen}
          footerContent={!isSingleBill && <UpgradeStepStrategy {...upgradeStepStrategyProps} footerContent />}
          footerActions={!isSingleBill && <UpgradeStepStrategy {...upgradeStepStrategyProps} footerActions />}
          onDismiss={closeDialog}
        >
          {hasScheduledDowngrade && (
            <div className="scheduled-downgrade-banner" data-qe-id="scheduled-downgrade-banner">
              <div className="scheduled-downgrade-banner__text">
                <div className="scheduled-downgrade-banner__title">
                  {t('upgrade-popup.scheduled-downgrade-banner.title')}
                </div>
                <div className="scheduled-downgrade-banner__body">
                  {t('upgrade-popup.scheduled-downgrade-banner.body')}{' '}
                  <Button
                    kind="link"
                    size="small"
                    onClick={cancelScheduledDowngrade}
                    disabled={isScheduledDowngradeLoading}
                    data-qe-id="scheduled-downgrade-banner-cancel-button"
                    className="scheduled-downgrade-banner__link"
                    text={t('upgrade-popup.scheduled-downgrade-banner.cancel-button')}
                  />
                </div>
              </div>
            </div>
          )}
          <UpgradeStepStrategy {...upgradeStepStrategyProps} />
        </Dialog>
      )}
    </>
  );
};

FeatureUpgradeDialog.defaultProps = {
  stepOneInformation: null,
  usage: null,
  launchPoint: null,
  featureRequirements: null,
  paymentSuccessCallback: () => {},
  unsavedChangesInCallingPage: false,
  isFullScreen: false,
  wrapClassName: null,
  setCloseDialog: () => {},
  setIsOpen: () => {},
  returnURL: null,
  returnLabel: null,
  calledFromDevPage: false,
  promoCode: null,
};

FeatureUpgradeDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  // optional:
  setIsOpen: PropTypes.func,
  // optional:
  // Similar to "setIsOpen" but more accurately named and will send a boolean param to indicate if upgrade succeeded or not.
  // Useful for caller to refresh page and entitlements upon dialog close.
  setCloseDialog: PropTypes.func,
  feature: PropTypes.string.isRequired,
  activity: PropTypes.string.isRequired,
  usage: PropTypes.number,
  // featureRequirements array is optional
  featureRequirements: PropTypes.arrayOf(
    PropTypes.shape({
      feature: PropTypes.string.isRequired,
      activity: PropTypes.string.isRequired,
      usage: PropTypes.number,
    })
  ),
  launchPoint: PropTypes.string,
  unsavedChangesInCallingPage: PropTypes.bool,
  isFullScreen: PropTypes.bool,
  paymentSuccessCallback: PropTypes.func,
  // optional, used to return from the first time pay flow
  returnURL: PropTypes.string,
  returnLabel: PropTypes.string,
  stepOneInformation: PropTypes.shape({
    featureName: PropTypes.string.isRequired,
    header: PropTypes.string.isRequired,
    subHeader: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    // optional:
    featureList: PropTypes.arrayOf(PropTypes.string),
  }),
  wrapClassName: PropTypes.string,
  calledFromDevPage: PropTypes.bool,
  promoCode: PropTypes.string,
};

export default FeatureUpgradeDialog;
